import { digger } from '@cbre/digger';

import { Amount } from 'uom';
import { formatNumber, isMasked } from '../misc/sanitizers';
import { DASH, EMPTY_STRING, TWO_DECIMALS_FORMAT } from '../misc/strings.constants';
import { symbolRetriever } from './currency/symbol';
import { areaUomAbbrev } from './uom-abbreviation';

/**
 * Returns '4 SF' or '$4 USD Dollar` if prefix `$` provided
 */
export const formatUomValues = (value, uom, prefix = '', suffix = '') =>
  `${prefix}${value != null ? value : DASH} ${uom != null ? uom : DASH}${suffix}`;

export interface UomFormat {
  /**
   * Specify the path where the value should be
   * value has precedence
   */
  valuePath: string;
  /**
   * Specify the path where the uom value should be
   * uom property has precedence
   */
  uomPath: string;
  /**
   * The value to be formatted
   */
  value: any;
  /**
   * The uom value
   */
  uom: string;
  prefix: string;
  suffix: string;
  defaultTxt: string;
  /**
   * Used by angular formatNumber function to return the number value formatted
   * by locale
   */
  location: string;
  digits: string;
}

const defaultUomFormat: UomFormat = {
  valuePath: '',
  uomPath: '',
  value: null,
  uom: '',
  prefix: '',
  suffix: '',
  defaultTxt: DASH,
  location: 'en-US',
  digits: '.0-2',
};

/**
 * Extract field value and field uom and return a formatUomValues response
 */
export const formatValueWithUom = (formatConfig: Partial<UomFormat>, data?: { [key: string]: any }) => {
  const options = { ...defaultUomFormat, ...formatConfig };
  const value = options.value || digger(data, options.valuePath);
  const uom = options.uom || digger(data, options.uomPath);
  return value != null
    ? formatUomValues(
        formatNumber(value, options.location, options.digits),
        areaUomAbbrev(uom),
        options.prefix,
        options.suffix
      )
    : options.defaultTxt;
};

const defaultCurrencyFormat: UomFormat = {
  valuePath: '',
  uomPath: '',
  value: null,
  uom: '',
  prefix: '$',
  suffix: '',
  defaultTxt: DASH,
  location: 'en-US',
  digits: TWO_DECIMALS_FORMAT,
};

/**
 * Formats currency fields (fields with uom holding rates/prices etc)
 * in the next pattern `$#,###.##`
 * @param formatConfig
 * @param data
 * @returns formatted currency field
 */
export const formatUomCurrency = (formatConfig: Partial<UomFormat>, data?: { [key: string]: any }): string => {
  const options = { ...defaultCurrencyFormat, ...formatConfig };
  const value = getValue(options, data);
  if (isMasked(value)) {
    return value;
  }
  const uom = getUom(options, data);
  // Blank rates need to show ‘-' instead of '0’
  if (isEmpty(value) && isEmpty(uom)) {
    return DASH;
  }

  const prefix = symbolRetriever(uom);
  return !isEmpty(value) ? formatValue(value, prefix, options) : getDefaultValue(prefix, options);
};

const getValue = (options: UomFormat, data?: Record<string, any>): any =>
  options.value || digger(data, options.valuePath);

const getUom = (options: UomFormat, data?: Record<string, any>): any => options.uom || digger(data, options.uomPath);

const isEmpty = (value: any): boolean => value == null || value === EMPTY_STRING;

const formatValue = (value: any, prefix: string, options: UomFormat): string => {
  return formatUomValues(
    formatNumber(value, options.location, options.digits || TWO_DECIMALS_FORMAT),
    EMPTY_STRING,
    prefix,
    options.suffix
  );
};

const getDefaultValue = (prefix: string, options: UomFormat): string | null =>
  formatUomValues(options.defaultTxt, EMPTY_STRING, prefix, options.suffix);

/**
 * It uses `formatNumber` a number and `areaUomAbbrev` to return `<number> <uom>`
 * @see Amount.Amount
 *
 * @param amount `Amount.Amount` from 'uom'
 * @param locale string default `'en-US'`
 * @param digits string default `'.0-0'`
 */
export const formatAmountObject = (amount: Amount.Amount<any>, locale = 'en-US', digits = '.0-0') =>
  `${formatNumber(amount.value, locale, digits)} ${areaUomAbbrev(amount.unit.name)}`;
